'use client';

import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Size, useComponentSize } from './useComponentSize';

interface ImageDimensionsHook {
    imageDimensions: Size | undefined;
    containerRef: (element: HTMLElement | null) => void;
    containerSize: Size;
    onImageLoad: React.ReactEventHandler<HTMLImageElement>;
    setImageRatio: Dispatch<SetStateAction<number>>;
}

export const useImageSizeCalculation = ({
    otherElementsHeight = 0,
    otherElementsWidth = 0
}: {
    otherElementsHeight?: number;
    otherElementsWidth?: number;
}): ImageDimensionsHook => {
    const [imageRatio, setImageRatio] = useState<number>(1);
    const [containerSize, containerRef] = useComponentSize();
    const [imageDimensions, setImageDimensions] = useState<Size>();

    const onImageLoad: React.ReactEventHandler<HTMLImageElement> = ({ currentTarget }) =>
        setImageRatio(currentTarget.naturalHeight / currentTarget.naturalWidth);

    useEffect(() => {
        /**  We want portrait images to have the height of the container size and then calculate the width from the image aspect ratio
         Opposite for landscape: We want the full container width and then calculate the height from the aspect ratio

         Special case: Nearly square/square images. Their calculation depends on the screen size.
         The following approach always starts with the limitation by height. Then we check, if the image fits in the container 
         - if not, we recalculate the dimensions by width. */

        const calculateDimensions = () => {
            const { height, width } = containerSize;
            const tempDimensions = {
                height: Math.floor(height - otherElementsHeight),
                width: Math.floor((height - otherElementsHeight) / imageRatio)
            };
            if (tempDimensions.height > height || tempDimensions.width > width)
                return {
                    width: Math.floor(width - otherElementsWidth),
                    height: Math.floor(width * imageRatio)
                };
            else return tempDimensions;
        };

        setImageDimensions(calculateDimensions());
    }, [imageRatio, containerSize, otherElementsHeight, otherElementsWidth]);

    return { imageDimensions, containerRef, onImageLoad, setImageRatio, containerSize };
};
